'use client'

import FiltersMenu from '@modules/serp/filters-menu'
import MobileBottomBar from '@modules/serp/mobile-bottom-bar'
import Sidebar from '@modules/serp/sidebar'

import { Suspense, useEffect, useRef, useState } from 'react'

import type { JobItem, JobSearch, PaginatedResponse } from '@types'

import useAddEventListener from '@hooks/useAddEventListener'
import { serpFiltersOpenedEvent, serpPageViewEvent } from '@lib/analytics-events'

import List from './jobs-list'

import styles from './serp.module.scss'

type JobResultsProps = {
  search: JobSearch
  jobs?: PaginatedResponse<JobItem[]>
  children: React.ReactNode
}

const Serp = ({ search, jobs, children }: JobResultsProps): JSX.Element => {
  const [isMobileSearchFiltersOpen, setIsMobileSearchFiltersOpened] = useState(false)
  const [isFiltersBarVisible, setIsFiltersBarVisible] = useState<boolean>(false)
  const listRef = useRef<HTMLDivElement>(null)
  const { page } = search

  useAddEventListener('scroll', () => {
    if (listRef.current) {
      setIsFiltersBarVisible(window.scrollY > 300 && listRef.current?.getBoundingClientRect().bottom > 300)
    }
  })

  const onMobileFiltersButtonClick = (): void => {
    serpFiltersOpenedEvent()
    setIsMobileSearchFiltersOpened(true)
  }

  useEffect(() => {
    if (search.job.micro || search.location.id) {
      serpPageViewEvent(search, jobs?.totalElements)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, jobs?.totalElements])

  return (
    <>
      <div className={styles.container}>
        <Sidebar search={search} />
        {jobs && jobs?.totalElements > 0 && (
          <List
            paginatedJobs={jobs}
            onFiltersButtonClick={onMobileFiltersButtonClick}
            ref={listRef}
            paginationInfo={{
              page: page,
              total: jobs?.totalElements ?? 0,
            }}
          />
        )}
        {jobs?.totalElements === 0 && children}
      </div>
      <Suspense>
        <FiltersMenu
          search={search}
          isOpened={isMobileSearchFiltersOpen}
          onClose={(): void => setIsMobileSearchFiltersOpened(false)}
        />
      </Suspense>
      <MobileBottomBar onFiltersButtonClick={onMobileFiltersButtonClick} isVisible={isFiltersBarVisible} />
    </>
  )
}

export default Serp
